import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle,
  FileText,
  Clock,
  Calendar,
  DollarSign,
  HelpCircle,
  AlertCircle,
  Download,
  ExternalLink,
  Calculator,
  BarChart,
  Receipt,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import MainLayout from "@/components/layouts/MainLayout";

const GSTRegistration = () => {
  return (
    <>
      <Helmet>
        <title>GST Registration | Numberz Insight</title>
        <meta
          name="description"
          content="Register for Goods and Services Tax (GST) with Numberz Insight. Professional assistance with GST registration and compliance."
        />
        <meta
          name="keywords"
          content="GST registration, Goods and Services Tax, tax registration, tax agent, Numberz Insight, Australian taxation"
        />
      </Helmet>

      <MainLayout>
        <main className="flex-grow">
          {/* Hero Section */}
          <section className="relative bg-gradient-to-b from-primary/10 to-background dark:from-primary/5 dark:to-background py-16 md:py-24">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-left"
                >
                  <Badge
                    variant="outline"
                    className="mb-4 px-3 py-1 border-primary/30 text-primary"
                  >
                    <Clock className="w-3 h-3 mr-1 inline" /> Expert GST
                    Assistance
                  </Badge>
                  <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
                    GST Registration & Compliance
                  </h1>
                  <p className="text-xl text-muted-foreground mb-6">
                    Navigate Australia's GST system with confidence using our
                    professional registration and compliance services.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button size="lg" className="gap-2">
                      Start GST Registration <ArrowRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      className="gap-2"
                      asChild
                    >
                      <Link to="/contact">Get Expert Advice</Link>
                    </Button>
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="rounded-xl overflow-hidden shadow-xl"
                >
                  <img
                    src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                    alt="Business professional reviewing financial documents"
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              </div>
            </div>
          </section>

          {/* Overview Section */}
          <section className="py-16 bg-muted/30 dark:bg-muted/10">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Understanding GST</h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  The Goods and Services Tax (GST) is a 10% tax on most goods,
                  services, and other items sold or consumed in Australia
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle>When to Register for GST</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-start">
                      <DollarSign className="h-5 w-5 mr-3 text-primary mt-0.5" />
                      <div>
                        <h3 className="font-medium">Turnover Threshold</h3>
                        <p className="text-muted-foreground">
                          You must register for GST if your business turnover is
                          $75,000 or more annually ($150,000 for non-profit
                          organizations).
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <Receipt className="h-5 w-5 mr-3 text-primary mt-0.5" />
                      <div>
                        <h3 className="font-medium">Taxi and Ride-Sourcing</h3>
                        <p className="text-muted-foreground">
                          You must register regardless of turnover if you
                          provide taxi travel or ride-sourcing services.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 mr-3 text-primary mt-0.5" />
                      <div>
                        <h3 className="font-medium">Voluntary Registration</h3>
                        <p className="text-muted-foreground">
                          You can choose to register even if your turnover is
                          below the threshold, which may be beneficial in
                          certain circumstances.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Benefits of GST Registration</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 mr-3 text-primary mt-0.5" />
                      <div>
                        <h3 className="font-medium">Claim GST Credits</h3>
                        <p className="text-muted-foreground">
                          Registered businesses can claim credits for GST paid
                          on business purchases and expenses.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 mr-3 text-primary mt-0.5" />
                      <div>
                        <h3 className="font-medium">
                          Professional Credibility
                        </h3>
                        <p className="text-muted-foreground">
                          GST registration can enhance your business's
                          credibility with customers and suppliers.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 mr-3 text-primary mt-0.5" />
                      <div>
                        <h3 className="font-medium">Business Growth</h3>
                        <p className="text-muted-foreground">
                          Proper GST management supports business growth and
                          helps avoid compliance issues.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Alert className="mt-8 border-primary/20">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Important Note</AlertTitle>
                <AlertDescription>
                  Failing to register for GST when required can result in
                  penalties and back-payment obligations. Our team can help
                  determine if you need to register and assist with the process.
                </AlertDescription>
              </Alert>
            </div>
          </section>

          {/* Services Section */}
          <section className="py-16">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Our GST Services</h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Comprehensive GST solutions for Australian businesses
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-primary" />
                      GST Registration
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Complete assistance with GST registration, including
                      determining eligibility, preparing documentation, and
                      submitting applications.
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                      <li>ABN and GST registration</li>
                      <li>Registration timing advice</li>
                      <li>Business structure considerations</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Calculator className="h-5 w-5 mr-2 text-primary" />
                      GST Calculations & Reporting
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Accurate calculation and reporting of GST obligations to
                      ensure compliance with ATO requirements.
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                      <li>BAS preparation and lodgment</li>
                      <li>GST calculation assistance</li>
                      <li>Record-keeping systems</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <BarChart className="h-5 w-5 mr-2 text-primary" />
                      GST Planning & Strategy
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Strategic advice on GST matters to optimize your
                      business's tax position and ensure compliance.
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                      <li>Cash flow management</li>
                      <li>GST-exclusive pricing strategies</li>
                      <li>Industry-specific GST advice</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* Process Section */}
          <section className="py-16 bg-muted/30 dark:bg-muted/10">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">
                  GST Registration Process
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Our streamlined approach to GST registration
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card>
                  <CardHeader className="text-center">
                    <div className="mx-auto bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                      <span className="text-xl font-bold text-primary">1</span>
                    </div>
                    <CardTitle>Initial Assessment</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-muted-foreground">
                      We evaluate your business activities and turnover to
                      determine GST registration requirements.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="text-center">
                    <div className="mx-auto bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                      <span className="text-xl font-bold text-primary">2</span>
                    </div>
                    <CardTitle>Documentation</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-muted-foreground">
                      We prepare all necessary documentation, including ABN
                      verification if already registered.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="text-center">
                    <div className="mx-auto bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                      <span className="text-xl font-bold text-primary">3</span>
                    </div>
                    <CardTitle>Registration</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-muted-foreground">
                      We submit your GST registration to the ATO and follow up
                      to ensure timely processing.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="text-center">
                    <div className="mx-auto bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                      <span className="text-xl font-bold text-primary">4</span>
                    </div>
                    <CardTitle>Implementation</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-muted-foreground">
                      We help set up your GST accounting systems and provide
                      guidance on ongoing compliance.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-12 text-center">
                <Button size="lg" className="gap-2">
                  Start GST Registration <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-16">
            <div className="container max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">
                  Frequently Asked Questions
                </h2>
                <p className="text-xl text-muted-foreground">
                  Common questions about GST registration and compliance
                </p>
              </div>

              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="item-1">
                  <AccordionTrigger>
                    How do I know if I need to register for GST?
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground">
                      You must register for GST if your annual turnover is
                      $75,000 or more ($150,000 for non-profit organizations),
                      or if you provide taxi or ride-sourcing services
                      regardless of turnover. If you're approaching these
                      thresholds, you should register within 21 days of reaching
                      them. Our team can help assess your specific situation and
                      advise on the best course of action.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2">
                  <AccordionTrigger>
                    What is the difference between ABN and GST registration?
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground">
                      An Australian Business Number (ABN) is a unique identifier
                      for your business and is required for most business
                      activities. GST registration is specifically for the Goods
                      and Services Tax system and requires you to have an ABN
                      first. While all businesses with an annual turnover of
                      $75,000+ must register for GST, not all businesses with an
                      ABN need to register for GST if they're below this
                      threshold.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3">
                  <AccordionTrigger>
                    How often do I need to report and pay GST?
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground">
                      GST reporting and payment frequency depends on your
                      business size and preferences. Options include:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-muted-foreground mt-2">
                      <li>
                        <strong>Quarterly</strong> - Most common, with BAS due
                        28 days after the end of each quarter
                      </li>
                      <li>
                        <strong>Monthly</strong> - Required for businesses with
                        turnover over $20 million, optional for others
                      </li>
                      <li>
                        <strong>Annually</strong> - Available for some smaller
                        businesses with specific eligibility criteria
                      </li>
                    </ul>
                    <p className="text-muted-foreground mt-2">
                      We can help determine the most suitable reporting
                      frequency for your business and assist with timely
                      lodgments.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-4">
                  <AccordionTrigger>
                    Can I claim GST credits for all business purchases?
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground">
                      Not all business purchases qualify for GST credits. You
                      can generally claim GST credits for purchases that:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-muted-foreground mt-2">
                      <li>Include GST in the price</li>
                      <li>Are for use in your business</li>
                      <li>Are supported by a valid tax invoice</li>
                    </ul>
                    <p className="text-muted-foreground mt-2">
                      Some items have special rules, such as entertainment
                      expenses, and certain purchases are GST-free or
                      input-taxed. Our team can provide guidance on what you can
                      and cannot claim.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-5">
                  <AccordionTrigger>
                    What happens if I don't register for GST when required?
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground">
                      Failing to register for GST when required can result in:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-muted-foreground mt-2">
                      <li>Penalties and interest charges</li>
                      <li>
                        Obligation to pay GST on sales made since the date you
                        should have registered
                      </li>
                      <li>
                        Potential limitations on claiming GST credits for that
                        period
                      </li>
                      <li>Increased scrutiny from the ATO</li>
                    </ul>
                    <p className="text-muted-foreground mt-2">
                      It's important to address any registration issues
                      promptly. Our team can help with voluntary disclosures and
                      remediation strategies if you've missed your registration
                      deadline.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-6">
                  <AccordionTrigger>
                    Is there a fee for GST registration?
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground">
                      There is no government fee for GST registration itself.
                      However, our professional service fees apply for our
                      assistance with the registration process, ensuring it's
                      completed correctly and efficiently. Our fees include
                      assessment of your GST obligations, preparation of all
                      necessary documentation, submission to the ATO, and
                      initial guidance on GST compliance. Please contact us for
                      our current fee structure.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 bg-primary/5 dark:bg-primary/10">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-3xl font-bold mb-4">
                    Ready to Register for GST?
                  </h2>
                  <p className="text-xl text-muted-foreground mb-6">
                    Our team of tax professionals is ready to help you with your
                    GST registration and compliance needs. Contact us today to
                    get started.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-3 text-primary" />
                      <span>Expert guidance on GST obligations</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-3 text-primary" />
                      <span>Streamlined registration process</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-3 text-primary" />
                      <span>Ongoing GST compliance support</span>
                    </div>
                  </div>
                </div>
                <div>
                  <Card>
                    <CardHeader>
                      <CardTitle>Start Your GST Registration</CardTitle>
                      <CardDescription>
                        Fill out the form below and one of our tax professionals
                        will contact you shortly.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <Button className="w-full" size="lg">
                          Start Registration
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full"
                          size="lg"
                          asChild
                        >
                          <Link to="/contact">Contact Us</Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </section>

          {/* Resources Section */}
          <section className="py-16">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">
                  Additional Resources
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Helpful information about GST registration and compliance
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-primary" />
                      ATO GST Resources
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Official information about GST from the Australian
                      Taxation Office.
                    </p>
                    <Button variant="outline" className="w-full" asChild>
                      <a
                        href="https://www.ato.gov.au/business/gst/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Visit ATO Website{" "}
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Calculator className="h-5 w-5 mr-2 text-primary" />
                      GST Calculator
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Tools to help calculate GST for your business
                      transactions.
                    </p>
                    <Button variant="outline" className="w-full" asChild>
                      <a
                        href="https://www.ato.gov.au/calculators-and-tools/business-viability-assessment-tool/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Use Calculator <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-primary" />
                      Business.gov.au
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Government resources for businesses, including GST
                      information.
                    </p>
                    <Button variant="outline" className="w-full" asChild>
                      <a
                        href="https://business.gov.au/registrations/register-for-taxes/register-for-goods-and-services-tax-gst"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Visit Business.gov.au{" "}
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>
        </main>
      </MainLayout>
    </>
  );
};

export default GSTRegistration;
